import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useMediaQuery, useTheme } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import Action_icon from '../../images/table/Actions_icon.svg';
import "./DynamicTable.css";
import Skeleton from "@mui/material/Skeleton";
import { styled } from '@mui/material/styles';
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: "20px",
  overflow: "auto",
  borderRadius: "10px",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  '&::-webkit-scrollbar': {
    width: "8px",
    height: "8px",
    borderRadius: "10px",
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: "#647490",
    borderRadius: "10px",
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: "#B7BFCC",
    borderRadius: "10px",
    margin: "5px",
  },
  scrollbarWidth: "thin",
  scrollbarColor: "#647490 #B7BFCC",
}));

const ResponsiveTableCell = styled(TableCell)(({ theme }) => ({
  padding: "8px 12px",
  minWidth: "120px",
  maxWidth: "300px",
  [theme.breakpoints.down('sm')]: {
    padding: "6px 8px",
    fontSize: "0.75rem",
    minWidth: "80px",
    maxWidth: "150px",
  },
}));

const ActionContainer = styled('div')(() => ({
  display: "flex",
  justifyContent: "flex-end",
}));

const ActionMenu = styled('div')(() => ({
  position: "fixed",
  zIndex: 1300,
  backgroundColor: "#fff",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
  borderRadius: "8px",
  minWidth: "160px",
  maxWidth: "200px",
  overflow: "visible",
}));

export default function DynamicTable({
  headers = [],
  data = [],
  onActionClick,
  actions = [],
  loading = false,
  skeletonRowCount = 5,
  translationNamespace = 'common',
  columnWidths = {}, // New prop for custom column widths
}) {
  const [activeRow, setActiveRow] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, placement: 'bottom' });
  const actionMenuRef = useRef(null);
  const actionIconRefs = useRef([]);
  const tableContainerRef = useRef(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation(translationNamespace);

  // Handle click outside and scroll events
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target) &&
        !actionIconRefs.current.some(ref => ref?.contains(event.target))
      ) {
        setActiveRow(null);
      }
    };

    const handleScroll = () => {
      setActiveRow(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // Calculate action menu position
  useEffect(() => {
    if (activeRow === null || !actionIconRefs.current[activeRow]) return;

    const iconRect = actionIconRefs.current[activeRow].getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const menuHeight = 44 * actions.length + 10;
    const menuWidth = 200;

    const spaceBelow = viewportHeight - iconRect.bottom;
    const spaceAbove = iconRect.top;
    const placement = spaceBelow >= menuHeight || spaceBelow > spaceAbove ? 'bottom' : 'top';

    let top = placement === 'bottom' ? iconRect.bottom + 5 : iconRect.top - menuHeight - 5;
    let left = iconRect.right - menuWidth + 20;

    // Prevent menu from going off-screen
    left = Math.max(10, Math.min(left, viewportWidth - menuWidth - 10));
    top = Math.max(10, Math.min(top, viewportHeight - menuHeight - 10));

    setMenuPosition({ top, left, placement });
  }, [activeRow, actions.length]);

  // Handle action clicks
  const handleActionClick = (event, rowIndex) => {
    event.stopPropagation();
    setActiveRow(activeRow === rowIndex ? null : rowIndex);
  };

  const handleActionItemClick = (actionId, rowIndex) => {
    if (onActionClick) {
      const actionObj = actions.find(action => action.id === actionId);
      onActionClick(actionId, rowIndex, actionObj);
    }
    setActiveRow(null);
  };

  // Initialize action icon refs
  useEffect(() => {
    actionIconRefs.current = Array(data.length).fill(null);
  }, [data.length]);

  const isActionsColumn = (header) => header.key === ACTIONS_COLUMN_ID;

  // Render skeleton rows
  const renderSkeletonRows = () => {
    return Array(skeletonRowCount).fill(0).map((_, rowIndex) => (
      <TableRow
        key={`skeleton-${rowIndex}`}
        sx={{
          height: "50px",
          "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
          borderBottom: "1px solid #ddd",
        }}
      >
        {headers.map((header, colIndex) => (
          <ResponsiveTableCell
            key={`skeleton-${rowIndex}-${colIndex}`}
            align={isActionsColumn(header) ? "right" : "left"}
            sx={{ width: columnWidths[header.key] || "auto" }}
          >
            {isActionsColumn(header) ? (
              <Skeleton variant="circular" width={20} height={20} sx={{ ml: "auto" }} />
            ) : (
              <Skeleton variant="text" width={`${60 + Math.random() * 40}%`} />
            )}
          </ResponsiveTableCell>
        ))}
      </TableRow>
    ));
  };

  return (
    <ResponsiveTableContainer ref={tableContainerRef}>
      <Table
        sx={{
          tableLayout: "auto",
          width: "100%",
          minWidth: isSmallScreen ? "100%" : "800px",
        }}
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: "#647490" }}>
            {headers.map((header, index) => (
              <ResponsiveTableCell
                key={header.key || index}
                sx={{
                  backgroundColor: "#647490",
                  color: "white",
                  fontWeight: "bold",
                  textAlign: isActionsColumn(header) ? "right" : "left",
                  borderTopLeftRadius: index === 0 ? "10px" : 0,
                  borderTopRightRadius: index === headers.length - 1 ? "10px" : 0,
                  borderBottom: "none",
                  whiteSpace: "normal",
                  width: columnWidths[header.key] || "auto",
                }}
              >
                {loading ? (
                  <Skeleton width="80%" />
                ) : isActionsColumn(header) ? (
                  t('Actions')
                ) : (
                  t(header.value)
                )}
              </ResponsiveTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            renderSkeletonRows()
          ) : data.length > 0 ? (
            data.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  height: "50px",
                  "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
                  borderBottom: "1px solid #ddd",
                }}
              >
                {headers.map((header, colIndex) => (
                  <ResponsiveTableCell
                    key={`${rowIndex}-${header.key || colIndex}`}
                    align={isActionsColumn(header) ? "right" : "left"}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: columnWidths[header.key] || "auto",
                    }}
                  >
                    {isActionsColumn(header) ? (
                      <ActionContainer>
                        <div
                          className="action-icon"
                          ref={(el) => (actionIconRefs.current[rowIndex] = el)}
                          onClick={(e) => handleActionClick(e, rowIndex)}
                          style={{ cursor: "pointer" }}
                        >
                          <img
                            style={{ width: "20px", height: "20px" }}
                            src={Action_icon}
                            alt={t('actions')}
                          />
                        </div>
                      </ActionContainer>
                    ) : (
                      <Tooltip
                        title={String(row[header.key] || '')}
                        disableHoverListener={String(row[header.key] || '').length < 20}
                      >
                        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {row[header.key] || '-'}
                        </span>
                      </Tooltip>
                    )}
                  </ResponsiveTableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <ResponsiveTableCell
                colSpan={headers.length}
                sx={{ textAlign: "center", height: "100px" }}
              >
                {t('noDataAvailable')}
              </ResponsiveTableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {activeRow !== null && (
        <ActionMenu
          ref={actionMenuRef}
          className="action-menu"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
          }}
        >
          {actions.map((action) => (
            <div
              key={action.id}
              className={`action-menu-item ${action.className || ''}`}
              onClick={() => handleActionItemClick(action.id, activeRow)}
              style={{
                padding: "10px 15px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                whiteSpace: "nowrap",
              }}
            >
              {action.icon && <img src={action.icon} alt={t(action.label)} style={{ width: "16px", height: "16px" }} />}
              {t(action.label)}
            </div>
          ))}
        </ActionMenu>
      )}
    </ResponsiveTableContainer>
  );
}